import { Document } from "models/schemas";
import {
  DocumentCollection,
  VersionedDocument,
  createDocumentCollection,
} from "../documents-table/version-utils";
import { StagedFile } from "./definitions";

/**
 * Safely retrieves all versions for a file with fallback logic
 * @param documentCollection The document collection containing version groups
 * @param stagedFile The staged file to get versions for
 * @returns Array of all versions for the file, or single-item array with fallback
 */
export const getAllVersionsForFile = (
  documentCollection: DocumentCollection | null,
  stagedFile: StagedFile
): VersionedDocument[] => {
  if (!documentCollection || !stagedFile.originalDocument) {
    return stagedFile.originalDocument
      ? [stagedFile.originalDocument as VersionedDocument]
      : [];
  }
  return documentCollection.getVersionsForDocument(stagedFile.originalDocument);
};

/**
 * Builds a merged document collection from existing documentCollection and staged files
 * Useful for checking version relationships when adding new files
 * @param documentCollection The existing document collection
 * @param stagedFiles Array of staged files
 * @param parentId Parent ID for the documents
 * @returns Merged DocumentCollection or null if no documents
 */
export const createMergedDocumentCollection = (
  documentCollection: DocumentCollection | null,
  stagedFiles: StagedFile[]
): DocumentCollection | null => {
  // 1. Add existing documents from documentCollection
  const allDocsForVersionCheck = documentCollection
    ? Array.from(documentCollection.versionGroupsMap.values()).flatMap(
        (group) => group.allVersions
      )
    : [];

  // 2. Add existing staged files (non-new, not deleted)
  const existingStagedDocs = stagedFiles
    .filter((sf) => !sf.isNew && !sf.toDelete && sf.originalDocument)
    .map((sf) => ({
      ...sf.originalDocument!,
      fileName: sf.fileName,
    }));

  const mergedDocs = [...allDocsForVersionCheck, ...existingStagedDocs];
  return mergedDocs.length > 0 ? createDocumentCollection(mergedDocs) : null;
};

/**
 * Filters staged files for display by removing deleted files and older versions
 * @param stagedFiles Array of staged files to filter
 * @returns Filtered array containing only displayable files
 */
export const filterDisplayFiles = (stagedFiles: StagedFile[]): StagedFile[] => {
  return stagedFiles.filter((f) => {
    // Filter out deleted files
    if (f.toDelete) return false;

    // For files with originalDocument, only show if isLatest is true or undefined
    // This hides older versions that were added for rename/delete operations
    if (f.originalDocument && f.isLatest === false) return false;

    return true;
  });
};

/**
 * Ensures all versions are present in staged files array, adding missing ones
 * Used when performing operations on all versions (rename, delete)
 * @param stagedFiles Current array of staged files
 * @param versions Array of versions that should be included
 * @returns Updated staged files array with all versions included
 */
export const ensureVersionsInStagedFiles = (
  stagedFiles: StagedFile[],
  versions: VersionedDocument[]
): StagedFile[] => {
  if (versions.length <= 1) {
    return stagedFiles;
  }

  const existingIds = new Set(stagedFiles.map((f) => f.id));
  const newFilesToAdd: StagedFile[] = [];

  versions.forEach((version) => {
    if (!existingIds.has(version.documentId)) {
      newFilesToAdd.push(createStagedFileFromDocument(version));
    }
  });

  return [...stagedFiles, ...newFilesToAdd];
};

/**
 * Converts a Document/VersionedDocument to StagedFile format
 * @param document The document to convert
 * @param additionalProps Optional additional properties to add to the staged file
 * @returns StagedFile object
 */
export const createStagedFileFromDocument = (
  document: Document | VersionedDocument,
  additionalProps?: Partial<StagedFile>
): StagedFile => {
  const isVersioned = "isLatest" in document;

  return {
    id: document.documentId,
    fileName: document.fileName,
    documentType: document.documentType,
    isNew: false,
    originalDocument: document,
    ...(isVersioned && {
      isLatest: (document as VersionedDocument).isLatest,
      versionNumber: (document as VersionedDocument).versionNumber,
      totalVersions: (document as VersionedDocument).totalVersions,
    }),
    ...additionalProps,
  };
};

/**
 * Handles cascade deletion of versioned files
 * When deleting a file with versions, all versions must be marked for deletion
 * @param docsToDelete Array of documents to delete
 * @param documentCollection The current document collection
 * @param stagedFiles Current array of staged files
 * @returns Updated staged files array with all versions marked for deletion
 */
export const cascadeDeleteVersionedFiles = (
  docsToDelete: StagedFile[],
  documentCollection: DocumentCollection | null,
  stagedFiles: StagedFile[]
): StagedFile[] => {
  const allDocsToDelete: StagedFile[] = [];

  docsToDelete.forEach((doc) => {
    if (doc.isNew) {
      // New files don't have versions, just mark for deletion
      allDocsToDelete.push(doc);
    } else if (documentCollection && doc.originalDocument) {
      // Existing files with versions - get all versions for cascade delete
      const allVersions = getAllVersionsForFile(documentCollection, doc);

      allVersions.forEach((version: VersionedDocument) => {
        const existingStagedFile = stagedFiles.find(
          (f) => f.id === version.documentId
        );
        if (existingStagedFile) {
          allDocsToDelete.push(existingStagedFile);
        } else {
          // Create staged file for version not in current display
          allDocsToDelete.push(
            createStagedFileFromDocument(version, { toDelete: true })
          );
        }
      });
    } else {
      allDocsToDelete.push(doc);
    }
  });

  const idsToDelete = new Set(allDocsToDelete.map((doc) => doc.id));
  const updatedStagedFiles = stagedFiles.map((f) =>
    idsToDelete.has(f.id) ? { ...f, toDelete: true } : f
  );

  const existingIds = new Set(stagedFiles.map((f) => f.id));
  const newFilesToAdd = allDocsToDelete.filter(
    (doc) => !existingIds.has(doc.id)
  );

  return [...updatedStagedFiles, ...newFilesToAdd];
};
